// audit-safety-integrated
import { readJson } from '../utils/auditSafety';
import { useState, useEffect } from 'react';
import { Package } from 'lucide-react';
import { API_BASE_URL } from '../services/api';

const TAB_LABELS = {
  grocery: 'Grocery',
  dailie: 'Veggies & Daily',
};

const QuantityDemand = () => {
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('');

  useEffect(() => {
    fetchDepartments();
    fetchDemand('');
  }, []);

  useEffect(() => {
    fetchDemand(selectedDepartment);
  }, [selectedDepartment]);

  const fetchDepartments = async () => {
    try {

      const res = await fetch(`${API_BASE_URL}/user/departments`, {
        headers: {  'Content-Type': 'application/json' },
      });
      const result = await readJson(res);
      if (!result.error) {
        setDepartments(result.data || []);
      }
    } catch (err) {
      void 0;
    }
  };

  const fetchDemand = async (deptId) => {
    setLoading(true);
    setError('');
    setData([]);
    setActiveTab('');
    try {

      let url = `${API_BASE_URL}/item/store-demand`;
      if (deptId) url += `?department=${deptId}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {  'Content-Type': 'application/json' },
      });
      const result = await readJson(response);
      if (!result.error) {
        const rows = result.data || [];
        setData(rows);
        const categories = [...new Set(rows.map(item => item.category))].filter(Boolean);
        if (categories.length > 0) setActiveTab(categories[0]);
      } else {
        setError(result.message || 'Failed to fetch demand data');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const categories = [...new Set(data.map(item => item.category))].filter(Boolean);
  const filteredData = activeTab ? data.filter(item => item.category === activeTab) : data;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center gap-2 mb-6">
        <Package className="w-5 h-5 text-blue-600" />
        <h1 className="text-xl font-semibold text-gray-800">Quantity Demand</h1>
      </div>

      {/* Department Dropdown */}
      <div className="mb-4">
        <select
          value={selectedDepartment}
          onChange={(e) => setSelectedDepartment(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-full max-w-xs"
        >
          <option value="">All Departments</option>
          {departments.map((dept) => (
            <option key={dept.id} value={dept.id}>{dept.name}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Tabs */}
        {!loading && !error && categories.length > 0 && (
          <div className="flex border-b border-gray-200 px-6">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveTab(cat)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === cat
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {TAB_LABELS[cat] || cat}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-500 text-sm">Loading...</span>
            </div>
          )}

          {error && !loading && (
            <div className="text-center py-12 text-red-500 text-sm">{error}</div>
          )}

          {!loading && !error && data.length === 0 && (
            <div className="text-center py-12 text-gray-400 text-sm">No demand data found.</div>
          )}

          {!loading && !error && filteredData.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 font-medium text-gray-600">S.No</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Avg Monthly Qty</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((row, index) => (
                    <tr key={row.id ?? index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                      <td className="px-4 py-3 text-gray-700">{row.name ?? '-'}</td>
                      <td className="px-4 py-3 text-gray-700 text-right">{row.avg_monthly_qty != null ? parseFloat(row.avg_monthly_qty).toFixed(2) : '-'}</td>
                      <td className="px-4 py-3 text-gray-500">{row.unit ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuantityDemand;
